import {
    Flex,
    HStack,
    Avatar,
    VStack,
    Box,
    Text,
    Button,
    IconButton,
    Icon,
    Image,
    useBreakpointValue,
} from '@chakra-ui/react';
import { FiChevronRight } from 'react-icons/fi';
import { useIntl } from 'react-intl';
import { Link, ScrollRestoration, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/auth/authStore';
import { ReactNode } from 'react';
import LanguageSelectMenu from '../ui/LanguageSelectMenu';
import { FaQuestion } from 'react-icons/fa';

export const AboutHeader = () => {
    const navigation = useNavigate();
    const intl = useIntl();
    const user = useAuthStore((state) => state.user)!;

    return (
        <Flex px={4} alignItems="center" bg={'rgba(0, 0, 0, 0.24)'} justifyContent={{ base: 'space-between' }}>
            <Box display={'flex'} alignItems={'center'}>
                <Image src={`${process.env.PUBLIC_URL}/logo_simple.png`} alt="logo" height={'30px'} />
                <IconButton
                    icon={<Icon as={FaQuestion} color={'gray.200'} />}
                    fontSize={'20px'}
                    aria-label="help"
                    variant={'link'}
                    ml={2}
                    as={Link}
                    to={'/docs/home'}
                ></IconButton>
            </Box>

            <HStack spacing={{ base: '0', md: '6' }} py={1}>
                <Flex alignItems={'center'}>
                    <LanguageSelectMenu />

                    {user ? (
                        <Button
                            onClick={() => navigation('/home')}
                            variant={'ghost'}
                            role="group"
                            py={2}
                            transition="all 0.3s"
                            _focus={{ boxShadow: 'none' }}
                        >
                            <HStack>
                                <Avatar size={'sm'} name={user.surname || user.firstName} />
                                <VStack display={{ base: 'none', md: 'flex' }} alignItems="flex-start" spacing="1px" ml="2">
                                    <Text
                                        _groupHover={{
                                            color: 'black',
                                        }}
                                        color={'white'}
                                        fontSize="sm"
                                    >{`${user.firstName} ${user.surname || ''}`}</Text>
                                    <Text
                                        fontSize="xs"
                                        _groupHover={{
                                            color: 'gray.800',
                                        }}
                                        color="gray.400"
                                    >
                                        {intl.formatMessage({ id: user.role })}
                                    </Text>
                                </VStack>
                                <Box display={{ base: 'none', md: 'flex' }}>
                                    <FiChevronRight />
                                </Box>
                            </HStack>
                        </Button>
                    ) : (
                        <Button onClick={() => navigation('/login')} variant={'text'} color={'white'}>
                            {intl.formatMessage({ id: 'logIn' })}{' '}
                        </Button>
                    )}
                </Flex>
            </HStack>
        </Flex>
    );
};

export const AboutLayout: React.FC<{ children: ReactNode, showBgCircles?: boolean }> = ({ children, showBgCircles = false }) => {
    const blurredCircleSize = useBreakpointValue({ 'base': 100, 'md': 100, 'lg': 100, 'xl': 200, '2xl': 500 });
    const circles = [
        { top: 500, left: 0 },
        { top: 1500, right: 0 },
        { top: 2500, left: 0 },
        { top: 3500, right: 0 },
        { top: 4500, left: 0 },
        { top: 5500, right: 0 },
        { top: 6500, left: 0 },
        { top: 7500, right: 0 },
    ];
    return (
        <Box minW={'100vw'} minH="100vh" bg={'surface.500'} position={'relative'}>
            {showBgCircles && circles.map((circle, index) => (
                <Box
                    key={index}
                    position="absolute"
                    width={blurredCircleSize}
                    height={blurredCircleSize}
                    background="rgba(255, 255, 255, 0.07)"
                    borderRadius="100%"
                    filter={`blur(${400}px)`}
                    {...circle}
                />
            ))}
            <AboutHeader />
            <ScrollRestoration />
            <Box>{children}</Box>
        </Box>
    );
};
